import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePagination } from "@/hooks/usePagination";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import { apiGet, apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { Pagination } from "@/components/ui/pagination";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { Stethoscope, CircleNotch } from "@phosphor-icons/react";

import { SessionCard } from "@/components/therapist/therapies/SessionCard";
import { RescheduleDialog } from "@/components/therapist/therapies/RescheduleDialog";
import { CancelSessionDialog } from "@/components/calendar";

import type { TherapistTherapySession, TherapiesResponse } from "@/types";

type StatusFilter = "" | "scheduled" | "completed" | "cancelled";

function isStatusFilter(value: unknown): value is Exclude<StatusFilter, ""> {
  return value === "scheduled" || value === "completed" || value === "cancelled";
}

export const Route = createFileRoute("/dashboard/therapist/therapies")({
  validateSearch: (search: Record<string, unknown>): { status?: Exclude<StatusFilter, ""> } =>
    isStatusFilter(search.status) ? { status: search.status } : {},
  component: TherapistTherapies,
});

const LIMIT = 20;

const LEGEND: { key: string; dot: string; label: string }[] = [
  { key: "scheduled", dot: "bg-primary", label: "Zakazana" },
  { key: "pending_outcome", dot: "bg-amber-500", label: "Čeka evidenciju" },
  { key: "completed", dot: "bg-emerald-600", label: "Završena" },
  { key: "cancelled", dot: "bg-destructive", label: "Otkazana" },
  { key: "no_show", dot: "bg-orange-500", label: "Nedolazak" },
];

function TherapistTherapies() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { status: initialStatus } = Route.useSearch();

  const TABS: { key: StatusFilter; label: string }[] = [
    { key: "", label: t("common.all") },
    { key: "scheduled", label: t("status.scheduledPlural") },
    { key: "completed", label: t("status.completedPlural") },
    { key: "cancelled", label: t("status.cancelledPlural") },
  ];

  const { page, setPage, filters, setFilter, pageSize } = usePagination<{ status: StatusFilter }>(
    { pageSize: LIMIT },
    { status: initialStatus ?? "" },
  );
  const { status } = filters;
  const [reschedulingSession, setReschedulingSession] = useState<
    { id: string; scheduledAt: string; therapistId: string; durationMinutes: number } | null
  >(null);
  const [cancelingSession, setCancelingSession] = useState<TherapistTherapySession | null>(null);

  const { data: meData } = useQuery({
    queryKey: ["therapist", "profile", "me"],
    queryFn: () => apiGet<{ id: string } | null>("/api/therapist/me", "").catch(() => null),
  });
  const myTherapistId = meData?.id;

  const invalidateTherapies = () =>
    queryClient.invalidateQueries({ queryKey: ["therapist", "therapies"] });

  const rescheduleMutation = useMutation({
    mutationFn: ({ id, date, time }: { id: string; date: string; time: string }) =>
      apiSend(`/api/sessions/${id}/reschedule`, "POST", { date, time }, t("therapist.therapies.errorRescheduling")),
    onSuccess: () => {
      invalidateTherapies();
      setReschedulingSession(null);
      toast.success(t("therapist.therapies.rescheduleSuccess"));
    },
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(`/api/sessions/${id}/complete`, "POST", {}, t("therapist.therapies.errorCompleting")),
    onSuccess: () => {
      invalidateTherapies();
      toast.success(t("therapist.therapies.completeSuccess"));
    },
  });

  const noShowMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(`/api/sessions/${id}/no-show`, "POST", {}, t("therapist.therapies.errorNoShow")),
    onSuccess: () => {
      invalidateTherapies();
      toast.success(t("therapist.therapies.noShowSuccess"));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiSend<{ pendingApproval?: boolean }>(`/api/sessions/${id}/cancel`, "POST", { reason }, t("therapist.therapies.errorCancelling")),
    onSuccess: (data) => {
      invalidateTherapies();
      setCancelingSession(null);
      toast.success(data?.pendingApproval ? "Zahtjev je poslan adminu na odobrenje" : t("therapist.therapies.cancelSuccess"));
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (requestId: string) =>
      apiSend(`/api/therapist/cancellation-requests/${requestId}/withdraw`, "POST", {}, "Greška pri povlačenju zahtjeva"),
    onSuccess: () => {
      invalidateTherapies();
      toast.success("Zahtjev je povučen");
    },
  });

  const query = useQuery({
    queryKey: ["therapist", "therapies", status, page],
    queryFn: () => {
      const params = new URLSearchParams({ status, page: String(page), limit: String(pageSize) });
      return apiGet<TherapiesResponse>(`/api/therapist/therapies?${params}`, t("therapist.therapies.errorLoading"));
    },
  });

  const { data: result, isLoading } = query;
  const sessions = result?.data ?? [];
  const total = result?.total ?? 0;
  const totalPages = result ? Math.ceil(total / LIMIT) : 1;
  const summary = result?.summary ?? { totalEarnings: 0, pendingPayout: 0, paidOut: 0 };
  const safeSummary = {
    totalEarnings: summary.totalEarnings ?? 0,
    pendingPayout: summary.pendingPayout ?? 0,
    paidOut: summary.paidOut ?? 0,
  };

  const handleReschedule = (session: TherapistTherapySession) => {
    setReschedulingSession({
      id: session.id,
      scheduledAt: session.scheduledAt,
      therapistId: session.therapistId || myTherapistId || "",
      durationMinutes: session.durationMinutes,
    });
  };

  const sessionWord =
    total === 1
      ? "sesija"
      : total % 10 >= 2 && total % 10 <= 4 && (total % 100 < 10 || total % 100 >= 20)
        ? "sesije"
        : "sesija";

  return (
    <div className="relative mx-auto max-w-6xl space-y-5 pb-14 sm:space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-2">
        <h1
          className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("therapist.therapies.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("therapist.therapies.subtitle")}</p>
      </header>

      {/* Stat band */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label={t("therapist.therapies.totalEarnings")} value={formatCurrency(safeSummary.totalEarnings)} tone="primary" />
        <StatCard label={t("therapist.therapies.pendingPayout")} value={formatCurrency(safeSummary.pendingPayout)} tone="pending" />
        <StatCard label={t("therapist.therapies.paidOut")} value={formatCurrency(safeSummary.paidOut)} tone="paid" />
      </section>

      {/* Control row */}
      <div className="flex flex-col gap-3 border-b border-border/60 pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-lg font-semibold leading-none tracking-tight text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
            {t("therapist.therapies.sessionsTitle")}
          </h2>
          <span className="text-xs font-medium tabular-nums text-muted-foreground">{total} {sessionWord}</span>
          {isLoading && <CircleNotch className="size-4 animate-spin text-muted-foreground" />}
        </div>

        <SegmentedFilter
          ariaLabel={t("therapist.therapies.sessionsTitle")}
          options={TABS.map((tabItem) => ({ value: tabItem.key, label: tabItem.label }))}
          value={status}
          onChange={(v) => setFilter("status", v)}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border/50 bg-card px-3 py-2 text-[11px] text-muted-foreground shadow-sm">
        {LEGEND.map((item) => (
          <div key={item.key} className="flex items-center gap-1.5">
            <span className={cn("size-2 rounded-full", item.dot)} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Sessions list */}
      <section>
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl border border-border/60 bg-card" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card py-16 text-center shadow-sm">
            <Stethoscope className="size-10 text-muted-foreground/25" weight="duotone" />
            <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground/70">
              {t("therapist.therapies.noSessions")}
            </p>
          </div>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {sessions.map((session) => (
                <li key={session.id}>
                  <SessionCard
                    session={session}
                    onReschedule={handleReschedule}
                    onComplete={(s) => completeMutation.mutate(s.id)}
                    onNoShow={(s) => noShowMutation.mutate(s.id)}
                    onCancel={(s) => setCancelingSession(s)}
                    onWithdrawCancelRequest={(s) => s.cancelRequestId && withdrawMutation.mutate(s.cancelRequestId)}
                    completePending={completeMutation.isPending}
                    noShowPending={noShowMutation.isPending}
                  />
                </li>
              ))}
            </ul>

            <div className="mt-6">
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </>
        )}
      </section>

      {reschedulingSession && (
        <RescheduleDialog
          session={reschedulingSession}
          onClose={() => setReschedulingSession(null)}
          onConfirm={(data) => rescheduleMutation.mutate(data)}
          isPending={rescheduleMutation.isPending}
        />
      )}

      {cancelingSession && (
        <CancelSessionDialog
          session={{
            id: cancelingSession.id,
            scheduledAt: cancelingSession.scheduledAt,
            clientName: cancelingSession.clientName,
            serviceName: cancelingSession.serviceName,
            durationMinutes: cancelingSession.durationMinutes,
            status: cancelingSession.status,
            paymentStatus: cancelingSession.paymentStatus,
            cancelRequestId: cancelingSession.cancelRequestId,
          }}
          onClose={() => setCancelingSession(null)}
          onConfirm={({ reason }) => cancelMutation.mutate({ id: cancelingSession.id, reason })}
          isPending={cancelMutation.isPending}
          requiresApproval={cancelingSession.paymentStatus === "paid"}
        />
      )}
    </div>
  );
}

const STAT_TONE: Record<"primary" | "pending" | "paid", { dot: string; value: string }> = {
  primary: { dot: "bg-primary", value: "text-foreground" },
  pending: { dot: "bg-amber-500", value: "text-amber-700" },
  paid: { dot: "bg-emerald-500", value: "text-emerald-700" },
};

function StatCard({ label, value, tone }: { label: string; value: string; tone: "primary" | "pending" | "paid" }) {
  const s = STAT_TONE[tone];
  return (
    <div className="rounded-2xl border border-border/60 bg-card px-5 py-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className={cn("size-1.5 rounded-full", s.dot)} />
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <p className={cn("mt-2 text-3xl font-semibold leading-none tabular-nums", s.value)} style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
    </div>
  );
}
